import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { ReactNode } from "react";

import { ApiError } from "../api/client";
import { Button } from "./Button";

type Tone = "error" | "success" | "info" | "warning";

const ICONS = { error: XCircle, success: CheckCircle2, info: Info, warning: AlertTriangle } as const;

export function Notice({ tone, children }: { tone: Tone; children: ReactNode }) {
  const Icon = ICONS[tone];
  // Errors interrupt (alert); confirmations are announced politely (status).
  return (
    <div className={`notice notice-${tone}`} role={tone === "error" ? "alert" : "status"}>
      <Icon size={18} className="notice-icon" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

/** A failed load, with a retry where retrying could help. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const retryable = !(error instanceof ApiError) || error.isRetryable;
  return (
    <div className="empty-state" role="alert">
      <span className="empty-state-icon empty-state-icon-danger" aria-hidden="true">
        <AlertTriangle size={24} />
      </span>
      <p className="empty-state-title">{errorMessage(error)}</p>
      {error instanceof ApiError && error.requestId ? (
        <p className="muted">If it keeps happening, quote reference {error.requestId}.</p>
      ) : null}
      {retryable && onRetry ? (
        <div className="empty-state-action">
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  icon?: ReactNode;
  /** One helpful sentence. */
  children?: ReactNode;
  /** The next step: usually one primary button or link. */
  action?: ReactNode;
  compact?: boolean;
}

/** Nothing to show yet: an icon, what this is, and what to do next. */
export function EmptyState({ title, icon, children, action, compact = false }: EmptyStateProps) {
  return (
    <div className={compact ? "empty-state empty-state-compact" : "empty-state"}>
      {icon ? (
        <span className="empty-state-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="empty-state-title">{title}</p>
      {children ? <div>{children}</div> : null}
      {action ? <div className="empty-state-action">{action}</div> : null}
    </div>
  );
}
